import * as React from 'react';
import { useWindowSize } from 'react-use';
import { Box, useTheme } from '@mui/material';

import { EnhancedTable, ZoneCardList } from 'components';
import { convertZonesToTableData } from 'utils';
import { useInactiveZonesTableRows } from 'pages/zones/zones.hook';
import { useParamValues } from 'hooks/url.hook';
import { useAppDispatch, useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { ZoneActivateDialog, ZoneDeleteDialog, ZoneUpdateDialog } from 'dialogs/zone';
import { updateLayout } from 'store/slices/layout.slice';

type State = {
  zoneId: number | null;
  visibleZoneActivateDialog: boolean;
  visibleZoneUpdateDialog: boolean;
  visibleZoneDeleteDialog: boolean;
};

export const BuildingZonesInactivePage = () => {
  const dispatch = useAppDispatch();
  const { isAdmin } = useAppSelector(selectCurrentUserInfo);
  const theme = useTheme();
  const { width } = useWindowSize();
  const { buildingId: buildingIdStr } = useParamValues(['buildingId']);
  const buildingId = buildingIdStr ? Number(buildingIdStr) : null;
  const [{ zoneId, visibleZoneActivateDialog, visibleZoneUpdateDialog, visibleZoneDeleteDialog }, setState] =
    React.useState<State>({
      zoneId: null,
      visibleZoneActivateDialog: false,
      visibleZoneUpdateDialog: false,
      visibleZoneDeleteDialog: false,
    });
  const [loading, columnKeys, columns, rows] = useInactiveZonesTableRows({
    buildingId: buildingId ? Number(buildingId) : null,
    setState,
  });

  React.useEffect(() => {
    dispatch(updateLayout({ headerTitle: 'Building Details' }));
  }, []);

  return (
    <>
      {width < theme.breakpoints.values.sm ? (
        <Box sx={{ paddingTop: 2 }}>
          <ZoneCardList
            zones={rows.sort((a, b) => (a.floorInfo > b.floorInfo ? 1 : a.floorInfo < b.floorInfo ? -1 : 0))}
            noDataLabel="No active zone"
          />
        </Box>
      ) : (
        <EnhancedTable
          config={{ toolbar: false, rowCheckable: false }}
          loading={loading}
          loadingLabel={'Loading active zones'}
          noDataLabel={'No active zones'}
          data={rows}
          convertFunctionForDataToTableData={convertZonesToTableData(isAdmin)}
          headColumns={columns}
          columnKeys={columnKeys}
          defaultSortColumnName="floorInfo"
          defaultSortDirection="asc"
          styles={{ tableStyle: { minWidth: 0 } }}
          initialRowsPerPage={10}
          visiblePagination
          rowActions={{}}
        />
      )}

      {typeof zoneId === 'number' && (
        <>
          <ZoneActivateDialog
            visible={visibleZoneActivateDialog}
            setVisible={(visibleZoneActivateDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleZoneActivateDialog }))
            }
            zoneId={zoneId}
          />

          <ZoneUpdateDialog
            visible={visibleZoneUpdateDialog}
            setVisible={(visibleZoneUpdateDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleZoneUpdateDialog }))
            }
            zoneId={zoneId}
          />

          <ZoneDeleteDialog
            visible={visibleZoneDeleteDialog}
            setVisible={(visibleZoneDeleteDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleZoneDeleteDialog }))
            }
            zoneId={zoneId}
          />
        </>
      )}
    </>
  );
};
